import { Button, Chip, Select } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Mail, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { ApiRequestError, apiDownload, apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { useListParams } from '@/lib/list/useListParams';

import {
  type SubmissionCounts,
  type SubmissionRow,
  SUBMISSION_STATUSES,
  type SubmissionStatus,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.7 — `/submissions`, the contact inbox.
 *
 * "Table (status · name · email · subject · date), a detail drawer with the
 * full message, status transitions, internal notes, 'reply via mail client'
 * (`mailto:` with a prefilled quote), CSV export, and a spam filter view. Show
 * the retention policy in the UI so staff know old records are purged."
 *
 * A drawer rather than a route, because the thing staff do here is triage: open,
 * read, mark, move to the next one. A page per message would make that four
 * navigations each.
 */
export function SubmissionsPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams('-created_at');

  const [open, setOpen] = useState<SubmissionRow | null>(null);

  const counts = useQuery({
    queryKey: ['admin', 'submissions', 'counts'],
    queryFn: () => apiFetch<SubmissionCounts>('/admin/submissions/counts'),
  });

  const columns: Column<SubmissionRow>[] = [
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'new' ? 'navy' : row.status === 'spam' ? 'gold' : 'neutral'}>
          {t(`submissions.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'name',
      label: t('submissions.columns.from'),
      render: (row) => (
        <div className="min-w-52">
          <span className="font-semibold text-primary">{row.name}</span>
          <span className="mt-0.5 block truncate text-caption text-muted">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      label: t('submissions.columns.subject'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {t(`submissions.subjects.${row.subject}`, row.subject)}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: t('submissions.columns.received'),
      sortable: true,
      render: (row) => (
        <span className="text-caption text-secondary">
          {row.created_at ? new Date(row.created_at).toLocaleString(locale) : '—'}
        </span>
      ),
    },
  ];

  const retention = counts.data?.retention;

  return (
    <>
      <ResourceListScreen
        resource="submissions"
        title={t('nav.items.submissions')}
        intro={
          retention
            ? t('submissions.introWithRetention', {
                spam: retention.spam_days,
                archived: retention.archived_months,
              })
            : t('submissions.intro')
        }
        columns={columns}
        labelFor={(row) => `${row.name} — ${row.email}`}
        defaultSort="-created_at"
        emptyTitle={t('submissions.empty.title')}
        emptyBody={t('submissions.empty.body')}
        // No create: a submission is what a visitor sent. `ResourceListScreen`
        // hides the button because §3 grants no `submissions.create`.
        newLabel={t('submissions.new')}
        deleteTitle={t('submissions.delete.title')}
        deleteBody={(label) => t('submissions.delete.body', { label })}
        onOpen={setOpen}
        secondaryAction={
          <Button
            type="button"
            size="sm"
            variant="secondary"
            iconLeft={<Download size={16} strokeWidth={1.75} aria-hidden="true" />}
            // Fetched with the bearer token and handed to the browser as a
            // blob: a plain link would arrive unauthenticated, and a 401 on a
            // navigation replaces the admin with a JSON error page.
            onClick={() => {
              void apiDownload('/admin/submissions/export', list.params, 'submissions.csv');
            }}
          >
            {t('submissions.export')}
          </Button>
        }
        filters={
          <Select
            label={t('content.columns.status')}
            hideLabel
            value={String(list.params.status ?? '')}
            onChange={(event) => list.setFilter('status', event.target.value || null)}
            options={[
              // The default excludes spam server-side, which is why this option
              // reads "everything except spam" rather than "all".
              { value: '', label: t('submissions.exceptSpam') },
              ...SUBMISSION_STATUSES.map((status) => ({
                value: status,
                label: `${t(`submissions.statuses.${status}`)}${
                  counts.data?.data[status] ? ` (${counts.data.data[status]})` : ''
                }`,
              })),
            ]}
          />
        }
      />

      {open ? (
        <SubmissionDrawer
          submission={open}
          onClose={() => setOpen(null)}
          onSaved={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

/** §5.7's detail drawer. */
function SubmissionDrawer({
  submission,
  onClose,
  onSaved,
}: {
  submission: SubmissionRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<SubmissionStatus>(submission.status);
  const [notes, setNotes] = useState(submission.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/admin/submissions/${submission.id}`, {
        method: 'PUT',
        body: { status, notes: notes.trim() === '' ? null : notes },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'submissions'] });
      onSaved();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    },
  });

  /*
   * §5.7's "reply via mail client (`mailto:` with a prefilled quote)".
   *
   * Built here rather than on the server: the mail client is on this machine,
   * and the quote has to be what the person on this screen is reading. The
   * original is quoted with `>` because that is what every mail client renders
   * as a quotation, and the reply lands under it.
   */
  const quoted = submission.message
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');

  const mailto = `mailto:${encodeURIComponent(submission.email)}?subject=${encodeURIComponent(
    t('submissions.replySubject', {
      subject: t(`submissions.subjects.${submission.subject}`, submission.subject),
    }),
  )}&body=${encodeURIComponent(`\n\n---\n${quoted}\n`)}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="submission-heading"
      className="fixed inset-y-0 end-0 z-40 flex w-full max-w-xl flex-col overflow-y-auto border-s border-subtle bg-surface shadow-xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-subtle p-5">
        <div>
          <h2 id="submission-heading" className="text-h4 font-extrabold text-primary">
            {submission.name}
          </h2>
          <p className="mt-1 text-caption text-muted">
            {submission.email}
            {submission.reference ? ` · ${submission.reference}` : ''}
          </p>
        </div>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <X size={16} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-5 p-5">
        <FormError message={error} />

        <dl className="grid grid-cols-2 gap-3 text-body-sm">
          <div>
            <dt className="text-caption text-muted">{t('submissions.columns.subject')}</dt>
            <dd className="text-primary">
              {t(`submissions.subjects.${submission.subject}`, submission.subject)}
            </dd>
          </div>

          <div>
            <dt className="text-caption text-muted">{t('submissions.columns.received')}</dt>
            <dd className="text-primary">
              {submission.created_at ? new Date(submission.created_at).toLocaleString(locale) : '—'}
            </dd>
          </div>
        </dl>

        {/*
          `whitespace-pre-wrap` and not rich text: this is what a visitor typed
          into a textarea, and rendering it as markup would be rendering a
          stranger's HTML in an authenticated admin session.
        */}
        <p className="bg-surface-subtle whitespace-pre-wrap rounded-card p-4 text-body text-primary">
          {submission.message}
        </p>

        <a
          href={mailto}
          className="inline-flex items-center gap-2 text-body-sm font-semibold text-navy-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          <Mail size={16} strokeWidth={1.75} aria-hidden="true" />
          {t('submissions.reply')}
        </a>

        <Select
          label={t('content.columns.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value as SubmissionStatus)}
          options={SUBMISSION_STATUSES.map((option) => ({
            value: option,
            label: t(`submissions.statuses.${option}`),
          }))}
        />

        <label className="block">
          <span className="mb-1.5 block text-body-sm font-semibold text-primary">
            {t('submissions.fields.notes')}
          </span>
          <span className="mb-1.5 block text-caption text-muted">
            {t('submissions.fields.notesHint')}
          </span>

          <textarea
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="w-full rounded-card border border-subtle bg-surface p-3 text-body-sm text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
          />
        </label>

        {submission.handled_by ? (
          <p className="text-caption text-muted">
            {t('submissions.handledBy', {
              name: submission.handled_by.name,
              at: submission.handled_at
                ? new Date(submission.handled_at).toLocaleString(locale)
                : '',
            })}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>

          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>

        {/* §9 keeps these for the abuse case: a burst of identical messages is
            a decision about spam, and that decision needs to see the address. */}
        {submission.ip_address ? (
          <p className="border-t border-subtle pt-4 text-caption text-muted">
            {t('submissions.origin', { ip: submission.ip_address })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
